import React from 'react';
import { oneOf, bool, number, string, object } from 'prop-types';
import { useTranslation } from 'react-i18next';

import useStyles from 'hooks/useStyles';
import { COURSE, REGULAR_CLASS } from 'constants/contentTypes';
import {
  PlayerPlayIcon,
  VideoDurationIcon,
  CalendarIcon,
} from 'components/Icon';

import styles from './ContentInfo.scss';

const getEmptyValue = (data, type) => {
  if (type === 'number') {
    return data || 0;
  }
  return data || '';
};

export default function ContentInfo({
  contentType,
  lessonCount,
  expireDate,
  reviewDays,
  isSemester,
  isOneTime,
  totalLessonsDurationTime,
}) {
  useStyles(styles);
  const { t } = useTranslation(['purchase']);
  const TimeIcon = contentType === COURSE ? VideoDurationIcon : CalendarIcon;
  const durationString = (() => {
    if (contentType === COURSE || isOneTime) {
      const { hours, minutes } = totalLessonsDurationTime;
      const hourString = hours
        ? t('purchase.content.coursePlanCardDetails.totalLessonDurationItem1', {
            hour: getEmptyValue(hours, 'number'),
          })
        : '';
      const minuteString = minutes
        ? t('purchase.content.coursePlanCardDetails.totalLessonDurationItem2', {
            minute: getEmptyValue(minutes, 'number'),
          })
        : '';

      return hourString ? `${hourString} ${minuteString}` : minuteString;
    }

    if (isSemester)
      return t('purchase.content.semesterPlan.replayLogicSemester', {
        date: getEmptyValue(expireDate, 'string'),
      });

    const formatedReviewDays = getEmptyValue(reviewDays, 'number');
    return formatedReviewDays > 1
      ? t('purchase.content.subscriptions.replayLogicFixedDurationPlural', {
          day: formatedReviewDays,
        })
      : t('purchase.content.subscriptions.replayLogicFixedDurationSingular', {
          day: formatedReviewDays,
        });
  })();

  const lessonCountString = (() => {
    const count = getEmptyValue(lessonCount, 'number');
    if (contentType === COURSE) {
      return count > 1
        ? `${count} ${t(
            'purchase.content.coursePlanCardDetails.totalLessonAmountPlural',
          )}`
        : `${count} ${t(
            'purchase.content.coursePlanCardDetails.totalLessonAmountSingular',
          )}`;
    }

    return count > 1
      ? `${count} ${t(
          'purchase.content.subscriptions.totalEpisodeAmountPlural',
        )}`
      : `${count} ${t(
          'purchase.content.subscriptions.totalEpisodeAmountSingular',
        )}`;
  })();

  return (
    <div className={styles.root}>
      <div className={styles.info}>
        <PlayerPlayIcon className={styles.icon} />
        <span className={styles.text}>{lessonCountString}</span>
      </div>
      <div className={styles.info}>
        <TimeIcon className={styles.icon} />
        <span className={styles.text}>{durationString}</span>
      </div>
    </div>
  );
}

ContentInfo.propTypes = {
  contentType: oneOf([COURSE, REGULAR_CLASS]).isRequired,
  totalLessonsDurationTime: object.isRequired,
  lessonCount: number.isRequired,
  expireDate: string.isRequired,
  reviewDays: number.isRequired,
  isSemester: bool.isRequired,
  isOneTime: bool.isRequired,
};
